/**
 * Voranmeldung-Page (Sprint 4, Feb 2026).
 *
 * UStVA (DE) / UVA (AT) Voranmeldung mit Kennzahl-Vorschau und XML-Export.
 *
 * Workflow:
 *   1. Periode wählen (M/Q/J + Index/Jahr)
 *   2. Engine-Vorschau anzeigen (Kennzahlen-Liste + Plausi-Hinweise)
 *   3. „Als Entwurf speichern" → persistiert in vat_filings
 *   4. XML herunterladen → manueller Upload bei ELSTER/FinanzOnline
 *   5. „Als eingereicht markieren" → Status submitted (mit Bestätigungs-Nr.)
 *
 * KI/Settings-Pfad? Nein — rein deterministisch aus invoices+expenses.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { isLexUser } from "@/utils/lexDomain";
import LexUvaPanel from "@/components/lex/LexUvaPanel";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/utils/api";
import { downloadBlob, extractBlobError } from "@/utils/downloadBlob";
import {
  ReceiptText, Download, FileCheck2, RefreshCw, AlertTriangle,
  ArrowDown, ArrowUp, Lock, Calendar, Info, Pencil, Trash2,
} from "lucide-react";
import { useUserCountry } from "@/hooks/useUserCountry";
import AuditBreakdownPanel from "@/components/AuditBreakdownPanel";
import { XmlPreflightDialog } from "@/components/XmlPreflightDialog";
import { PlausiWarnings } from "@/components/voranmeldung/PlausiWarnings";
import { entityDetailUrl } from "@/utils/entityLinks";
import { PreflightChecklist } from "@/components/voranmeldung/PreflightChecklist";
import { getLegalRef } from "@/utils/legalRefs";


const STATUS_LABELS = { draft: "Entwurf", ready: "Prüfungsfertig", submitted: "Eingereicht" };
const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  ready: "bg-blue-100 text-blue-800 border-blue-200",
  submitted: "bg-emerald-100 text-emerald-800 border-emerald-200",
};


function fmtMoney(value, currency = "EUR") {
  if (value === null || value === undefined) return "—";
  return Number(value).toLocaleString("de-DE", { style: "currency", currency });
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("de-DE"); } catch { return iso; }
}


/** Period-Picker: erzeugt period_type + year + period_index Wahl. */
function PeriodPicker({ value, onChange }) {
  const currentYear = new Date().getFullYear();
  const { country } = useUserCountry();
  // AT kennt keine Jahres-UVA — U30-Zeiträume sind nur monatlich/quartalsweise
  const isAT = (country || "").toUpperCase() === "AT";

  useEffect(() => {
    if (isAT && value.period_type === "yearly") {
      onChange({ ...value, period_type: "quarterly", period_index: 1 });
    }
  }, [isAT, value.period_type]);

  const years = useMemo(() => {
    // 7 Jahre rückwärts für historische Voranmeldungen (Aufbewahrungsfristen)
    return Array.from({ length: 7 }, (_, i) => currentYear - i);
  }, [currentYear]);

  const monthLabels = [
    "Januar", "Februar", "März", "April", "Mai", "Juni",
    "Juli", "August", "September", "Oktober", "November", "Dezember",
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="voranmeldung-period-picker">
      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Zeitraum-Typ</Label>
        <Select
          value={value.period_type}
          onValueChange={(v) => onChange({ ...value, period_type: v, period_index: v === "yearly" ? null : 1 })}
        >
          <SelectTrigger data-testid="voranmeldung-period-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="monthly">Monatlich</SelectItem>
            <SelectItem value="quarterly">Quartalsweise</SelectItem>
            {!isAT && <SelectItem value="yearly">Jährlich</SelectItem>}
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label className="text-xs text-slate-600 mb-1.5 block">Jahr</Label>
        <Select value={String(value.year)} onValueChange={(v) => onChange({ ...value, year: Number(v) })}>
          <SelectTrigger data-testid="voranmeldung-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {value.period_type !== "yearly" && (
        <div>
          <Label className="text-xs text-slate-600 mb-1.5 block">
            {value.period_type === "monthly" ? "Monat" : "Quartal"}
          </Label>
          <Select
            value={String(value.period_index || 1)}
            onValueChange={(v) => onChange({ ...value, period_index: Number(v) })}
          >
            <SelectTrigger data-testid="voranmeldung-period-index">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {value.period_type === "monthly"
                ? monthLabels.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>)
                : [1, 2, 3, 4].map((q) => <SelectItem key={q} value={String(q)}>Q{q} ({["Jan–Mär", "Apr–Jun", "Jul–Sep", "Okt–Dez"][q - 1]})</SelectItem>)
              }
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}


/** Kennzahl-Tabelle */
function KennzahlenTable({ kennzahlen }) {
  if (!kennzahlen || kennzahlen.length === 0) {
    return <p className="text-sm text-slate-500 italic">Keine Kennzahlen für diese Periode.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="voranmeldung-kennzahlen-table">
        <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="text-left py-2.5 px-3 w-20">KZ</th>
            <th className="text-left py-2.5 px-3">Bezeichnung</th>
            <th className="text-right py-2.5 px-3">Wert</th>
          </tr>
        </thead>
        <tbody>
          {kennzahlen.map((k) => (
            <tr key={k.code} className="border-b border-slate-100" data-testid={`voranmeldung-kz-${k.code}`}>
              <td className="py-2 px-3 font-mono text-slate-900 font-medium">{k.code}</td>
              <td className="py-2 px-3 text-slate-700">
                {k.label}
                {k.is_tax_amount && <span className="ml-2 text-[10px] text-slate-400 uppercase">Steuer</span>}
                {k.note && <div className="text-xs text-slate-500 italic mt-0.5">{k.note}</div>}
              </td>
              <td className={`py-2 px-3 text-right font-mono tabular-nums ${k.is_tax_amount ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                {fmtMoney(k.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


/** Mark-Submitted-Dialog */
function SubmitDialog({ open, onClose, filing, onSubmitted }) {
  const [reference, setReference] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setReference("");
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post(`/voranmeldung/${filing.id}/mark-submitted`, { reference });
      toast.success("Als eingereicht markiert");
      onSubmitted?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Markieren");
    } finally {
      setSubmitting(false);
    }
  };

  if (!filing) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="voranmeldung-submit-dialog">
        <DialogHeader>
          <DialogTitle>Als eingereicht markieren</DialogTitle>
          <DialogDescription>
            {filing.country === "DE"
              ? "Trage die ELSTER-Bestätigungsnummer ein (Telenummer / Transferticket)."
              : "Trage die FinanzOnline-Bestätigungsnummer ein."}
          </DialogDescription>
        </DialogHeader>
        <div className="py-4">
          <Label htmlFor="reference" className="text-sm">Bestätigungsnummer (optional)</Label>
          <Input
            id="reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder={filing.country === "DE" ? "z.B. 2026-1234-5678" : "z.B. FON12345"}
            className="mt-1.5"
            data-testid="voranmeldung-reference-input"
          />
          <p className="text-xs text-slate-500 mt-2">
            Du kannst sie später nicht mehr ändern. Lass das Feld leer, wenn du sie noch nicht hast.
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="voranmeldung-confirm-submit"
          >
            <FileCheck2 className="h-4 w-4 mr-1.5" />
            {submitting ? "Markiere…" : "Bestätigen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function VoranmeldungPage() {
  const { country } = useUserCountry();
  const isCH = (country || "").toUpperCase() === "CH";
  const [period, setPeriod] = useState({
    period_type: "quarterly",
    year: new Date().getFullYear(),
    period_index: Math.floor((new Date().getMonth()) / 3) + 1,
  });
  const [computation, setComputation] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [filings, setFilings] = useState([]);
  const [activeFiling, setActiveFiling] = useState(null);
  const [computing, setComputing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("compute");
  // Pre-Flight-Check (Juni 2026): Download läuft über eine Checklisten-Prüfung
  const [preflightFiling, setPreflightFiling] = useState(null);
  const fetchPreflight = useCallback(
    async (filing) => (await api.get(`/voranmeldung/${filing.id}/preflight`)).data,
    [],
  );

  const loadFilings = useCallback(async () => {
    try {
      const res = await api.get("/voranmeldung");
      setFilings(res.data.filings || []);
    } catch (err) {
      // Non-fatal: noch keine UStVA-Filings angelegt — leerer Zustand wird korrekt gerendert.
      console.warn('[Voranmeldung] loadFilings failed:', err?.response?.status, err?.message);
    }
  }, []);

  useEffect(() => { loadFilings(); }, [loadFilings]);

  const handleCompute = async (overridePeriod) => {
    const p = overridePeriod || period;
    setComputing(true);
    setActiveFiling(null);
    setPreflight(null);
    try {
      const res = await api.post("/voranmeldung/compute", p);
      setComputation(res.data.computation);
      setPreflight(res.data.preflight || null);
      toast.success("Vorschau berechnet");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Berechnung fehlgeschlagen");
    } finally {
      setComputing(false);
    }
  };

  // Entwurf bearbeiten: Periode laden, in den Berechnen-Tab wechseln und neu berechnen.
  // Beim Speichern wird der bestehende Entwurf derselben Periode aktualisiert.
  const editDraft = (f) => {
    const d = new Date(f.period_start);
    const year = d.getUTCFullYear();
    const month = d.getUTCMonth() + 1;
    const period_index = f.period_type === "monthly" ? month : Math.floor((month - 1) / 3) + 1;
    const newPeriod = { period_type: f.period_type, year, period_index };
    setPeriod(newPeriod);
    setComputation(null);
    setActiveTab("compute");
    handleCompute(newPeriod);
  };

  const handleDelete = async (f) => {
    if (!window.confirm(`Entwurf „${f.period_label}" wirklich löschen?`)) return;
    try {
      await api.delete(`/voranmeldung/${f.id}`);
      toast.success("Entwurf gelöscht");
      if (activeFiling?.id === f.id) { setActiveFiling(null); setComputation(null); }
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const res = await api.post("/voranmeldung", period);
      setActiveFiling(res.data.filing);
      toast.success(res.data.updated_existing ? "Bestehender Entwurf aktualisiert" : "Entwurf gespeichert");
      loadFilings();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadXml = async (filing) => {
    try {
      const res = await api.get(`/voranmeldung/${filing.id}/xml`, { responseType: "blob" });
      downloadBlob(
        res,
        `${filing.country}_voranmeldung_${filing.period_label.replace(/\s/g, "_")}.xml`,
        "application/xml",
      );
      toast.success("XML heruntergeladen");
    } catch (e) {
      toast.error(await extractBlobError(e, "Download fehlgeschlagen"));
    }
  };

  const isSavedFiling = !!activeFiling;
  const showAuthority = computation?.country === "AT" ? "FinanzOnline" : "ELSTER";
  // lex.ivoliq: Navy/Gold-Design, Sie-Form, keine Emojis/Flaggen
  const { user } = useAuth();
  const lex = isLexUser(user);

  // lex (Sep 2026): Anwälte berechnen KEINE eigene UVA — die Kanzlei rechnet und gibt frei.
  if (lex) {
    return (
      <div className="space-y-6" data-testid="voranmeldung-page">
        <div>
          <div className="lex-gold-line mb-4" />
          <h1 className="lex-serif text-4xl sm:text-5xl font-semibold" style={{ color: "#0A1128" }}>Voranmeldung.</h1>
          <p className="text-sm text-slate-500 mt-2">
            Ihre Kanzlei berechnet und gibt jede UVA für Sie frei — Sie müssen nichts rechnen.
            Freigegebene Voranmeldungen samt Zahlungsanweisung und QR-Code finden Sie hier.
          </p>
        </div>
        <LexUvaPanel showEmpty />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="voranmeldung-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        {lex ? (
          <div>
            <div className="lex-gold-line mb-4" />
            <h1 className="lex-serif text-4xl sm:text-5xl font-semibold" style={{ color: "#0A1128" }}>Voranmeldung.</h1>
            <p className="text-sm text-slate-500 mt-2">
              Umsatzsteuer-Voranmeldung berechnen und als Übergabe-Datei (XML) für Ihre Kanzlei bzw. zur manuellen Übernahme in FinanzOnline erzeugen.
            </p>
          </div>
        ) : (
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-2">
            <ReceiptText className="h-7 w-7 text-blue-600" />
            {isCH ? "MwSt-Abrechnung" : "Voranmeldung"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {isCH
              ? "Quartalsweise Mehrwertsteuer-Abrechnung für die Eidgenössische Steuerverwaltung (ESTV)."
              : "Umsatzsteuer-Voranmeldung berechnen und als Übergabe-Datei (XML) für Steuerberater oder zur manuellen Übernahme in ELSTER (DE) bzw. FinanzOnline (AT) erzeugen."}
          </p>
        </div>
        )}
      </div>

      {/* Feb 2026 — CH: explizit als "kommende Version" kommunizieren statt API-Fehler */}
      {isCH && (
        <Card className="border-blue-200 bg-blue-50" data-testid="voranmeldung-ch-info">
          <CardContent className="p-5 flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900 space-y-2">
              <p className="font-semibold">Die Schweizer MwSt-Abrechnung ist in Vorbereitung.</p>
              <p>
                Die ESTV-konforme Quartals-Abrechnung (Saldosteuer- oder effektive Methode) inkl. Formular 0050 bzw. XML-Upload kommt in einer kommenden Version.
                Bis dahin kannst du deine MwSt-Daten weiterhin im Dokumente-Bereich sammeln und aus dem Einnahmen-/Ausgaben-Export deinem Treuhänder übergeben.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isCH && (
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={lex ? "bg-white border border-slate-200 rounded-none p-1" : "bg-slate-100"} data-testid="voranmeldung-tabs">
          <TabsTrigger value="compute" className={lex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : ""} data-testid="voranmeldung-tab-compute">
            <RefreshCw className="h-4 w-4 mr-1.5" />
            Berechnen
          </TabsTrigger>
          <TabsTrigger value="history" className={lex ? "rounded-none data-[state=active]:bg-[#0A1128] data-[state=active]:text-white" : ""} data-testid="voranmeldung-tab-history">
            <Calendar className="h-4 w-4 mr-1.5" />
            Verlauf ({filings.length})
          </TabsTrigger>
        </TabsList>

        {/* ── Tab 1: Compute ── */}
        <TabsContent value="compute" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Periode wählen</CardTitle>
              <CardDescription>
                {lex
                  ? "Wählen Sie Zeitraum, Jahr und Quartal/Monat. Die Engine zieht Honorarnoten + Ausgaben aus dem gewählten Zeitraum."
                  : "Wähle Zeitraum, Jahr und Quartal/Monat. Die Engine zieht Rechnungen + Ausgaben aus dem gewählten Zeitraum."}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <PeriodPicker value={period} onChange={setPeriod} />
              <Button
                onClick={() => handleCompute()}
                disabled={computing}
                className={lex ? "bg-[#0A1128] hover:bg-black rounded-none" : "bg-blue-600 hover:bg-blue-700"}
                data-testid="voranmeldung-compute-btn"
              >
                <RefreshCw className={`h-4 w-4 mr-1.5 ${computing ? "animate-spin" : ""}`} />
                {computing ? "Berechne…" : "Vorschau berechnen"}
              </Button>
            </CardContent>
          </Card>

          {computation && (
            <Card data-testid="voranmeldung-result-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-base flex items-center gap-2">
                      {lex
                        ? (computation.country === "DE" ? "UStVA" : "UVA")
                        : (computation.country === "DE" ? "🇩🇪 UStVA" : "🇦🇹 UVA")} ·{" "}
                      <span className="text-slate-700 font-normal">
                        {fmtDate(computation.period_start)} – {fmtDate(computation.period_end)}
                      </span>
                    </CardTitle>
                    <CardDescription>
                      Steuerschema: <span className="font-mono">{computation.tax_scheme}</span> · {showAuthority}-konform
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {!isSavedFiling && (
                      <Button
                        variant="outline"
                        onClick={handleSaveDraft}
                        disabled={saving}
                        data-testid="voranmeldung-save-draft-btn"
                      >
                        {saving ? "Speichere…" : "Als Entwurf speichern"}
                      </Button>
                    )}
                    {isSavedFiling && (
                      <>
                        <Button
                          variant="outline"
                          onClick={() => setPreflightFiling(activeFiling)}
                          data-testid="voranmeldung-download-xml-btn"
                        >
                          <Download className="h-4 w-4 mr-1.5" />
                          XML herunterladen
                        </Button>
                        {activeFiling.status !== "submitted" && (
                          <Button
                            onClick={() => setSubmitDialogOpen(true)}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid="voranmeldung-mark-submitted-btn"
                          >
                            <FileCheck2 className="h-4 w-4 mr-1.5" />
                            Als eingereicht
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Summary KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Umsätze netto</div>
                    <div className="text-lg font-semibold tabular-nums">{fmtMoney(computation.revenue_net)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Umsatzsteuer</div>
                    <div className="text-lg font-semibold tabular-nums text-rose-700">+ {fmtMoney(computation.revenue_vat)}</div>
                  </div>
                  <div className="bg-slate-50 rounded-lg px-3 py-2 border border-slate-200">
                    <div className="text-xs text-slate-500">Vorsteuer</div>
                    <div className="text-lg font-semibold tabular-nums text-emerald-700">− {fmtMoney(computation.input_vat)}</div>
                  </div>
                  <div className={`rounded-lg px-3 py-2 border-2 ${computation.payable_vat >= 0 ? "border-rose-300 bg-rose-50" : "border-emerald-300 bg-emerald-50"}`}>
                    <div className="text-xs text-slate-600 uppercase">{computation.payable_vat >= 0 ? "Zahllast" : "Erstattung"}</div>
                    <div className={`text-xl font-semibold tabular-nums ${computation.payable_vat >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                      {computation.payable_vat >= 0 ? <ArrowUp className="h-4 w-4 inline mr-1" /> : <ArrowDown className="h-4 w-4 inline mr-1" />}
                      {fmtMoney(Math.abs(computation.payable_vat))}
                    </div>
                  </div>
                </div>

                {/* Plausi-Hinweise */}
                <PlausiWarnings
                  warnings={computation.warnings}
                  warningItems={computation.warning_items}
                  testId="voranmeldung-warnings"
                  onOpenItem={(w) => window.open(entityDetailUrl(w.entity_type, w.entity_id), "_blank", "noopener")}
                />

                {/* FinanzOnline/Elster Vorab-Check (schon in der Vorschau) */}
                <PreflightChecklist preflight={preflight} testId="voranmeldung-preflight" />

                {/* Kennzahlen */}
                <div>
                  <h3 className="text-sm font-medium text-slate-900 mb-2">Kennzahlen ({showAuthority}-Format)</h3>
                  <KennzahlenTable kennzahlen={computation.kennzahlen} />
                </div>

                {/* Feb 2026 — Audit-Breakdown / Quellen aufschlüsseln */}
                <AuditBreakdownPanel
                  breakdown={computation.audit_breakdown}
                  currencyFormatter={fmtMoney}
                  headerTitle="Kennzahlen-Quellen"
                  headerNote={
                    `Aufschlüsselung der Kennzahlen nach Quell-Kategorie (Rechnungen, manuelle Einnahmen, Vorsteuer-Belege, aktivierte Anlagen, ${getLegalRef(computation.country, "vorsteuer_berichtigung")}-Korrekturen, innergem. Erwerb).`
                  }
                />

                {isSavedFiling && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-900 space-y-1.5">
                    <div>
                      <Lock className="h-3.5 w-3.5 inline mr-1" />
                      Entwurf gespeichert (ID: <span className="font-mono">{activeFiling.id.slice(0, 8)}…</span>).
                      Beim Eingereicht-Markieren werden Werte eingefroren.
                    </div>
                    <div className="text-blue-800">
                      <strong>Hinweis:</strong> Das XML ist eine <strong>Übergabe-Datei zur manuellen Übernahme</strong> in
                      {showAuthority === "ELSTER" ? " Mein ELSTER" : " FinanzOnline"} bzw. zur Weitergabe an den Steuerberater.
                      Ein <em>echter Direkt-Upload via Datenstrom</em> (BMF-U30-Schema/ERiC) ist noch nicht enthalten —
                      {lex
                        ? " die Übermittlung erfolgt aktuell durch Sie selbst oder Ihre Kanzlei im Portal."
                        : " die Übermittlung erfolgt aktuell durch dich selbst oder deinen Steuerberater im Portal."}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ── Tab 2: History ── */}
        <TabsContent value="history" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {filings.length === 0 ? (
                <div className="py-16 text-center text-sm text-slate-500" data-testid="voranmeldung-history-empty">
                  <ReceiptText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  {lex
                    ? "Noch keine Voranmeldungen. Erstellen Sie Ihre erste oben im Tab „Berechnen“."
                    : "Noch keine Voranmeldungen. Erstelle deine erste oben im Tab „Berechnen“."}
                </div>
              ) : (
                <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-sm" data-testid="voranmeldung-history-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                    <tr>
                      <th className="text-left py-2.5 px-3">Periode</th>
                      <th className="text-left py-2.5 px-3">Land</th>
                      <th className="text-right py-2.5 px-3">Zahllast</th>
                      <th className="text-left py-2.5 px-3">Status</th>
                      <th className="text-left py-2.5 px-3">Eingereicht am</th>
                      <th className="text-right py-2.5 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filings.map((f) => (
                      <tr key={f.id} className="border-b border-slate-100" data-testid={`voranmeldung-row-${f.id}`}>
                        <td className="py-2.5 px-3 font-medium text-slate-900">{f.period_label}</td>
                        <td className="py-2.5 px-3">{lex ? f.country : (f.country === "DE" ? "🇩🇪 DE" : "🇦🇹 AT")}</td>
                        <td className={`py-2.5 px-3 text-right font-mono tabular-nums ${f.computation?.payable_vat >= 0 ? "text-rose-700" : "text-emerald-700"}`}>
                          {fmtMoney(f.computation?.payable_vat)}
                        </td>
                        <td className="py-2.5 px-3">
                          <Badge className={`${STATUS_STYLES[f.status]} border text-xs`}>
                            {STATUS_LABELS[f.status]}
                          </Badge>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600 text-xs">
                          {f.submitted_at ? fmtDate(f.submitted_at) : "—"}
                          {f.submission_reference && (
                            <div className="text-[10px] text-slate-400 font-mono">{f.submission_reference}</div>
                          )}
                        </td>
                        <td className="py-2.5 px-3 text-right space-x-1">
                          {f.status === "draft" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => editDraft(f)}
                              data-testid={`voranmeldung-edit-${f.id}`}
                              title="Entwurf bearbeiten"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPreflightFiling(f)}
                            data-testid={`voranmeldung-download-${f.id}`}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {f.status !== "submitted" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setActiveFiling(f); setSubmitDialogOpen(true); }}
                              data-testid={`voranmeldung-submit-${f.id}`}
                            >
                              <FileCheck2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {f.status === "draft" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(f)}
                              data-testid={`voranmeldung-delete-${f.id}`}
                              title="Entwurf löschen"
                              className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      )}

      <SubmitDialog
        open={submitDialogOpen}
        onClose={() => setSubmitDialogOpen(false)}
        filing={activeFiling}
        onSubmitted={loadFilings}
      />

      <XmlPreflightDialog
        filing={preflightFiling}
        onClose={() => setPreflightFiling(null)}
        fetchPreflight={fetchPreflight}
        onDownload={handleDownloadXml}
      />
    </div>
  );
}
